'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useCurrency } from '@/context/CurrencyContext';

interface MenuItem {
  label: string;
  url: string;
  children?: MenuItem[];
}

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // Currency integration
  const { currency, setCurrency } = useCurrency();
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);

  const currencies = [
    { code: 'AED', flagUrl: '/uploads/ae.png', countryCode: 'ae' },
    { code: 'USD', flagUrl: '/uploads/us.png', countryCode: 'us' },
    { code: 'RUB', flagUrl: '/uploads/ru.png', countryCode: 'ru' },
    { code: 'EUR', flagUrl: '/uploads/eu.png', countryCode: 'eu' },
  ];

  const activeOption = currencies.find((c) => c.code === currency) || currencies[0];

  useEffect(() => {
    fetchHeaderMenu();
  }, []);

  const buildMenuTree = (flatItems: any[]): MenuItem[] => {
    const tree: MenuItem[] = [];
    const levels: { [key: number]: MenuItem } = {};

    flatItems.forEach((item) => {
      const node: MenuItem = {
        label: item.label || '',
        url: item.url || '#',
        children: [],
      };

      const level = item.level || 0;
      levels[level] = node;

      if (level === 0) {
        tree.push(node);
      } else {
        // Find the closest parent of level - 1
        const parent = levels[level - 1];
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(node);
        } else {
          // If no parent found (e.g. invalid indentation sequence), treat as root
          tree.push(node);
        }
      }
    });

    return tree;
  };

  const fetchHeaderMenu = async () => {
    try {
      const res = await fetch('/api/menus');
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        // Look for a menu explicitly named 'header', 'navbar', or 'main'
        const headerMenu =
          data.find((m: any) => {
            const name = (m.name || '').toLowerCase();
            return name.includes('header') || name.includes('navbar') || name.includes('main');
          }) || data[0]; // fallback to first menu if only one exists

        if (headerMenu && headerMenu.items) {
          const parsed =
            typeof headerMenu.items === 'string' ? JSON.parse(headerMenu.items) : headerMenu.items;
          if (Array.isArray(parsed) && parsed.length > 0) {
            const nestedTree = buildMenuTree(parsed);
            setMenuItems(nestedTree);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching header menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const pathname = usePathname();
  const isHomepage = pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 font-[Montserrat,sans-serif] ${isHomepage ? 'shadow-none' : 'bg-white shadow-sm'}`}
      style={isHomepage ? { backgroundColor: 'rgb(212, 236, 185)' } : undefined}
    >
      <div className="container mx-auto px-6" style={{ maxWidth: '1480px' }}>
        <div className="flex items-center justify-between h-[85px]">
          {/* Logo */}
          <a href="/" className="flex-shrink-0">
            <Image
              src="/uploads/Plotae_logo_250px.svg"
              alt="Plotae logo"
              width={203}
              height={60}
              className="h-[60px] w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {loading ? (
              <div className="flex items-center gap-6 animate-pulse px-3 py-2.5">
                <div className="h-4.5 w-12 bg-slate-100/80 rounded-none border border-slate-200/20"></div>
                <div className="h-4.5 w-16 bg-slate-100/80 rounded-none border border-slate-200/20"></div>
                <div className="h-4.5 w-14 bg-slate-100/80 rounded-none border border-slate-200/20"></div>
                <div className="h-4.5 w-20 bg-slate-100/80 rounded-none border border-slate-200/20"></div>
                <div className="h-4.5 w-16 bg-slate-100/80 rounded-none border border-slate-200/20"></div>
              </div>
            ) : (
              menuItems.map((item, index) => {
                const hasChildren = item.children && item.children.length > 0;
                return (
                  <div
                    key={index}
                    className="relative group"
                    onMouseEnter={() => hasChildren && setActiveDropdown(item.label)}
                    onMouseLeave={() => hasChildren && setActiveDropdown(null)}
                  >
                    <a
                      href={item.url}
                      className="flex items-center gap-1 px-3 hover:opacity-80 transition-opacity duration-200"
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '16px',
                        fontWeight: 600,
                        lineHeight: '85px',
                        color: 'rgb(53, 74, 33)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {item.label}
                      {hasChildren && (
                        <svg
                          className="w-3 h-3 transition-transform duration-200 group-hover:rotate-180"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          style={{ color: 'rgb(53, 74, 33)' }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )}
                    </a>

                    {/* Dropdown Menu */}
                    {hasChildren && (
                      <div className="absolute top-full left-0 mt-0 min-w-[320px] bg-white border border-slate-200/80 shadow-xl rounded-none py-0 overflow-hidden divide-y divide-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                        {item.children?.map((child, cIndex) => {
                          const hasGrandChildren = child.children && child.children.length > 0;
                          return (
                            <div key={cIndex} className="relative group/sub">
                              <a
                                href={child.url}
                                className="navbar-submenu-link flex items-center justify-between px-6 py-4 whitespace-nowrap transition-all duration-150"
                              >
                                <span>{child.label.toUpperCase()}</span>
                                {hasGrandChildren && (
                                  <svg
                                    className="w-3 h-3 text-slate-400 group-hover/sub:text-white transform -rotate-90 ml-4 transition-colors"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                )}
                              </a>

                              {/* Grandchildren dropdown (Level 2) */}
                              {hasGrandChildren && (
                                <div className="absolute left-full top-0 min-w-[320px] bg-white border border-slate-200/80 shadow-xl rounded-none py-0 overflow-hidden divide-y divide-slate-100 opacity-0 invisible group-hover/sub:opacity-100 group-hover/sub:visible transition-all duration-150 z-50">
                                  {child.children?.map((grandChild, gIndex) => (
                                    <a
                                      key={gIndex}
                                      href={grandChild.url}
                                      className="navbar-submenu-link block px-6 py-4 whitespace-nowrap transition-all duration-150"
                                    >
                                      {grandChild.label.toUpperCase()}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Currency Selector Dropdown */}
            <div className="relative">
              <button
                onClick={() => setCurrencyDropdownOpen(!currencyDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-2 transition-opacity hover:opacity-85"
              >
                <img
                  src={activeOption.flagUrl}
                  alt={activeOption.code}
                  className="w-[20px] h-[14px] object-cover rounded-[1px] border border-[#006838]/10"
                />
                <svg
                  className={`w-3 h-3 transform transition-transform duration-200 ${currencyDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: 'rgb(53, 74, 33)' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {currencyDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setCurrencyDropdownOpen(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-32 bg-[#006838] border border-[#00502b] shadow-2xl z-50 py-1.5 rounded-none text-white animate-in fade-in duration-200">
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/20"></div>
                    {currencies.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => {
                          setCurrency(c.code);
                          setCurrencyDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-[11px] font-bold text-left transition-colors uppercase tracking-widest ${currency === c.code ? 'bg-[#004e2a] text-white font-black' : 'hover:bg-[#005a30]'}`}
                      >
                        <img
                          src={c.flagUrl}
                          alt={c.code}
                          className="w-[18px] h-[13px] object-cover rounded-[1px]"
                        />
                        <span>{c.code}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Data Intelligence Button */}
            <a
              href="https://digital.plotae.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 flex items-center bg-black text-white rounded-full px-5 py-2 text-[13px] font-bold uppercase tracking-wide hover:bg-black/90 transition-colors"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '13px',
                fontWeight: 700,
              }}
            >
              <span className="bg-[#006838] text-white rounded px-2 py-0.5 text-[11px] font-black mr-2">
                DATA
              </span>
              <span className="text-white">INTELLIGENCE</span>
            </a>
          </nav>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2"
            style={{ color: 'rgb(53, 74, 33)' }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-[60]">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-[#006838]/20 backdrop-blur-sm transition-opacity duration-300"
              onClick={() => setMobileMenuOpen(false)}
            ></div>
            
            {/* Drawer */}
            <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[360px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-100">
                <Image
                  src="/uploads/Plotae_logo_250px.svg"
                  alt="Plotae logo"
                  width={140}
                  height={40}
                  className="h-10 w-auto"
                />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 -mr-2 text-slate-400 hover:text-[#006838] hover:bg-slate-50 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Drawer Body - Scrollable */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
                <nav className="flex flex-col gap-2">
                  {loading ? (
                    <div className="flex flex-col gap-4 animate-pulse">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="h-10 w-full bg-slate-100 rounded-md"></div>
                      ))}
                    </div>
                  ) : (
                    menuItems.map((item, index) => {
                      const hasChildren = item.children && item.children.length > 0;
                      const isActive = activeDropdown === item.label;
                      return (
                        <div key={index} className="flex flex-col border-b border-slate-100/50 pb-2">
                          <div className="flex items-center justify-between">
                            <a
                              href={item.url}
                              className="py-3 text-[15px] font-bold text-[#222] uppercase tracking-wide hover:text-[#006838] transition-colors"
                              onClick={() => !hasChildren && setMobileMenuOpen(false)}
                            >
                              {item.label}
                            </a>
                            {hasChildren && (
                              <button
                                onClick={() => setActiveDropdown(isActive ? null : item.label)}
                                className={`p-2 rounded-full transition-colors ${isActive ? 'bg-[#006838]/10 text-[#006838]' : 'text-slate-400 hover:bg-slate-50'}`}
                              >
                                <svg
                                  className={`w-4 h-4 transform transition-transform duration-300 ${isActive ? 'rotate-180' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            )}
                          </div>

                          {/* Mobile Dropdown Items */}
                          {hasChildren && isActive && (
                            <div className="flex flex-col pl-4 mt-1 border-l-2 border-[#006838]/20 animate-in fade-in slide-in-from-top-2 duration-200">
                              {item.children?.map((child, cIndex) => {
                                const hasGrandChildren = child.children && child.children.length > 0;
                                return (
                                  <div key={cIndex} className="flex flex-col">
                                    <a
                                      href={child.url}
                                      className="py-2.5 text-[13px] font-semibold text-slate-600 hover:text-[#006838] uppercase tracking-wider transition-colors"
                                      onClick={() => !hasGrandChildren && setMobileMenuOpen(false)}
                                    >
                                      {child.label}
                                    </a>
                                    {hasGrandChildren && (
                                      <div className="pl-4 pb-2 flex flex-col gap-1 border-l border-slate-200/50 mt-1">
                                        {child.children?.map((grandChild, gIndex) => (
                                          <a
                                            key={gIndex}
                                            href={grandChild.url}
                                            className="py-2 text-[12px] font-medium text-slate-500 hover:text-[#006838] uppercase tracking-normal transition-colors"
                                            onClick={() => setMobileMenuOpen(false)}
                                          >
                                            {grandChild.label}
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </nav>

                {/* Mobile Currency Selector */}
                <div className="mt-8 mb-4 flex flex-col gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest text-center">
                    Select Currency
                  </span>
                  <div className="grid grid-cols-4 gap-2">
                    {currencies.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => setCurrency(c.code)}
                        className={`py-2.5 text-[10px] font-black rounded-lg transition-all flex flex-col items-center gap-1.5 ${currency === c.code ? 'bg-[#006838] text-white shadow-md scale-105' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#006838]/50'}`}
                      >
                        <img
                          src={c.flagUrl}
                          alt={c.code}
                          className="w-5 h-3.5 object-cover rounded-sm shadow-sm"
                        />
                        <span>{c.code}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Drawer Footer */}
              <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                <a
                  href="https://digital.plotae.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-gradient-to-r from-[#002e51] to-[#00182b] text-white rounded-full px-6 py-3.5 text-[14px] font-bold uppercase shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all"
                >
                  <span className="bg-white text-[#002e51] rounded-full px-2.5 py-0.5 text-[11px] font-black mr-2">
                    DATA
                  </span>
                  <span className="tracking-widest">INTELLIGENCE</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .navbar-submenu-link {
          font-family: 'Montserrat', sans-serif !important;
          font-size: 16px !important;
          font-weight: 400 !important;
          line-height: 24px !important;
          color: rgb(43, 43, 43) !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
        }
        .navbar-submenu-link:hover {
          color: white !important;
          background-color: #006838 !important;
        }
        .navbar-submenu-link:hover svg {
          color: white !important;
        }
      `,
        }}
      />
    </header>
  );
}
